import type { AppDispatch, State } from '~/store';
import {
  selectCurrentWorkflowHistoryEntry,
  selectIsExpressionActiveSubEditorOpen,
  selectIsRuleBuilderActiveSubEditorOpen,
  selectIsPendingHistoryCommit,
  selectWorkflowData,
  selectWorkflowProps,
  selectHasPendingExpressionGroup,
  selectRuleBuilderHasPendingGroup,
  selectIsMenuBarModalOpen,
} from '../selectors';
import {
  appendWorkflowHistoryEntry,
  applyHistoryTraversal,
  closeMenuBarModal,
  resetExpressionSubEditor,
  resetRuleBuilderSubEditor,
  setPendingWorkflowHistoryTransaction,
  clearPendingWorkflowHistoryTransaction,
  resetPendingGroup,
} from '../slice';
import { changeDescription } from '../types';
import type {
  WorkflowHistoryContext,
  WorkflowHistoryEntry,
  WorkflowStateChange,
  WorkflowStateSnapshot,
} from '../types';
import { diffWorkflowState, isUndo } from 'flow/service/history';
import type { SchemaInstance, WorkflowProps } from 'flow/store/engine/types';
import type { HistoryAction } from 'flow/service/history/constants';
import { trackHistoryTraversal } from 'flow/service/history/telemetry';
import { clearWorkflowHistoryFieldHighlights, isThresholdSubEditorArgs } from 'flow/editor/hooks/useWorkflowHistory/helpers';
import { resetRuleBuilderPendingGroupStateThunk } from './rules';

function cloneWorkflowState(state: State): WorkflowStateSnapshot {
  return {
    workflowData: structuredClone(selectWorkflowData(state)) as Partial<SchemaInstance>,
    workflowProps: structuredClone(selectWorkflowProps(state)) as WorkflowProps,
  };
}

export function createWorkflowHistoryThunk(
  /** A callback that mutates redux state by performing its own dispatch(es). */
  stateChange: WorkflowStateChange,
  shouldCreateHistoryEntry: boolean,
  context: WorkflowHistoryContext,
) {
  return (dispatch: AppDispatch, getState: () => State) => {
    if (!shouldCreateHistoryEntry) {
      stateChange();
      return;
    }

    if (selectIsPendingHistoryCommit(getState())) {
      stateChange();
      return;
    }

    const beforeState = cloneWorkflowState(getState());

    stateChange();

    const afterState = cloneWorkflowState(getState());

    const diff = diffWorkflowState(beforeState, afterState);

    if (diff.workflowData === undefined) {
      // History entries are anchored by a workflowData change.
      return;
    }

    const entry: WorkflowHistoryEntry = {
      diff: {
        workflowData: diff.workflowData,
        workflowProps: diff.workflowProps ?? null,
      },
      ...context,
    };

    dispatch(appendWorkflowHistoryEntry({ entry }));
  };
}

/** Resets ui state before triggering an undo / redo */
export function resetUiStateThunk(historyEntry: WorkflowHistoryEntry) {
  return (dispatch: AppDispatch, getState: () => State) => {
    clearWorkflowHistoryFieldHighlights();

    const state = getState();

    const isExpressionSubEditorOpen = selectIsExpressionActiveSubEditorOpen(state);
    const isRuleBuilderSubEditorOpen = selectIsRuleBuilderActiveSubEditorOpen(state);

    const { uiEffectContext } = historyEntry;


    const isSubEditorChange = 'subEditorArgs' in uiEffectContext;
    const isTargetingRuleBuilder = isSubEditorChange
      && isThresholdSubEditorArgs(uiEffectContext.subEditorArgs);

    if (selectHasPendingExpressionGroup(state)) {
      dispatch(resetPendingGroup());
    }

    if (selectRuleBuilderHasPendingGroup(state)) {
      dispatch(resetRuleBuilderPendingGroupStateThunk());
    }

    // reset the sub editors when necessary
    if (isExpressionSubEditorOpen && (!isSubEditorChange || isTargetingRuleBuilder)) {
      dispatch(resetExpressionSubEditor());
    }

    if (isRuleBuilderSubEditorOpen && (!isSubEditorChange || !isTargetingRuleBuilder)) {
      dispatch(resetRuleBuilderSubEditor());
    }

    // the failure handling and SDK config effects re-open their menu bar modal, so leave it
    // alone for those; any other change closes an open menu bar modal before navigating.
    const isMenuBarModalChange = uiEffectContext.target === changeDescription.failureHandlingMode
      || uiEffectContext.target === changeDescription.sdkConfig;
    if (selectIsMenuBarModalOpen(state) && !isMenuBarModalChange) {
      dispatch(closeMenuBarModal());
    }
  };
}

export function beginWorkflowHistoryTransaction() {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    if (selectIsPendingHistoryCommit(state)) {
      return false;
    }

    dispatch(setPendingWorkflowHistoryTransaction({
      beforeState: cloneWorkflowState(state),
    }));

    return true;
  };
}

export function commitWorkflowHistoryTransaction({
  context,
}: {
  context: WorkflowHistoryContext;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const { pendingBeforeState } = state.workflowEditor.editHistory;
    if (!pendingBeforeState) {
      return;
    }

    const afterState = cloneWorkflowState(state);
    const diff = diffWorkflowState(pendingBeforeState, afterState);

    if (diff.workflowData !== undefined) {
      dispatch(appendWorkflowHistoryEntry({
        entry: {
          diff: {
            workflowData: diff.workflowData,
            workflowProps: diff.workflowProps ?? null,
          },
          ...context,
        },
      }));
    }

    dispatch(clearPendingWorkflowHistoryTransaction());
  };
}

export function discardWorkflowHistoryTransaction() {
  return (dispatch: AppDispatch, getState: () => State) => {
    if (!selectIsPendingHistoryCommit(getState())) {
      return;
    }

    dispatch(clearPendingWorkflowHistoryTransaction());
  };
}

export function traverseHistoryThunk({
  historyAction,
  triggerUiEffect,
}: {
  historyAction: HistoryAction;
  triggerUiEffect?: (
    workflowHistoryEntry: WorkflowHistoryEntry,
    historyAction: HistoryAction,
  ) => void;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const undoEntry = selectCurrentWorkflowHistoryEntry(getState());

    dispatch(applyHistoryTraversal({ historyAction }));

    const redoEntry = selectCurrentWorkflowHistoryEntry(getState());

    const impactedEntry = isUndo(historyAction) ? undoEntry : redoEntry;

    dispatch(resetUiStateThunk(impactedEntry));

    trackHistoryTraversal({ entry: impactedEntry, historyAction });

    if (triggerUiEffect) {
      triggerUiEffect(impactedEntry, historyAction);
    }
  };
}
